import { useContent } from "../../Core/Content/Provider";
import type { PropsWithChildren, ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";

import { BrandConstants } from "../../Core/Constants/BrandConstants";
import { RouteConstants } from "../../Core/Constants/RouteConstants";
import { ButtonPiece } from "../UI/ButtonPiece";
import { isRouteActiveFn } from "./Functions";

const logoPath = "/cmj-cinema-logo.png";

interface NavigationItemModel {
  label: string;
  to: string;
}

export const BrandLogoPiece = ({ size = "compact" }: { size?: "compact" | "large" }) => (
  <Link className="inline-flex items-center gap-3" to={RouteConstants.home}>
    <img
      alt={`${BrandConstants.name} logo`}
      className={[
        "object-contain",
        size === "large" ? "h-28 w-28" : "h-16 w-16",
      ].join(" ")}
      src={logoPath}
    />
  </Link>
);

export const NavigationLinksPiece = ({
  currentPath,
  items,
  stacked = false,
}: {
  currentPath: string;
  items: NavigationItemModel[];
  stacked?: boolean;
}) => (
  <nav
    className={
      stacked
        ? "flex flex-col gap-3"
        : "hidden items-center gap-6 lg:flex"
    }
  >
    {items.map((item) => (
      <NavLink
        key={item.to}
        className={({ isActive }) =>
          [
            "text-sm font-black uppercase transition",
            isActive || isRouteActiveFn(currentPath, item.to)
              ? "text-cmj-gold"
              : "text-cmj-mist hover:text-white",
          ].join(" ")
        }
        to={item.to}
      >
        {item.label}
      </NavLink>
    ))}
  </nav>
);

export const FooterPiece = () => {
  const content = useContent("footer");
  return (
  <footer className="border-t-4 border-cmj-red bg-cmj-base text-white">
    <div className="section-shell py-9">
      <div className="guide-footer-contact">
        <BrandLogoPiece size="large" />
        <p className="max-w-sm text-xl uppercase leading-8">
          {content.tagline}
        </p>
        <a className="font-black" href={`tel:${content.phone}`}>
          {content.phone}
        </a>
        <a className="font-black uppercase" href={`mailto:${content.email}`}>
          {content.email}
        </a>
        <a className="font-black uppercase" href={content.website}>
          {content.websiteLabel}
        </a>
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        <ButtonPiece appearance="secondary" to={RouteConstants.booking}>
          Start Booking
        </ButtonPiece>
        <ButtonPiece appearance="ghost" to={RouteConstants.packages}>
          Review Packages
        </ButtonPiece>
      </div>
    </div>
  </footer>
);
};

export const PageShellPiece = ({
  header,
  children,
  footer,
}: PropsWithChildren<{
  header: ReactNode;
  footer: ReactNode;
}>) => (
  <div className="grain-overlay min-h-screen bg-[#f7f6f1]">
    {header}
    <main>{children}</main>
    {footer}
  </div>
);

export const PageHeaderPiece = ({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) => (
  <div className="cinema-page-heading">
    <div className="cinema-page-eyebrow"><span aria-hidden="true" />{eyebrow}<span className="cinema-page-edition">CMJ CINEMA / THE STUDIO</span></div>
    <h1>{title}</h1>
    <p>{description}</p>
    <div className="cinema-page-rule" aria-hidden="true"><span />✳</div>
  </div>
);
